import React, { useState } from "react";
import { formatDate, todayISO } from "./useDueDates";

/**
 * Sets one KPI's due date.
 *
 * Opened from the row's Configure button, mirroring how a unit is set on the
 * Unit tab. Setting a date is staged rather than saved outright, so setting
 * several KPIs still costs one request when Save all runs. When the KPI has its
 * own saved date (`ownId`), it can also be removed here — reverting the KPI to
 * any location/module deadline, or none.
 * @param {object} props
 * @param {object} props.kpi the KPI being configured.
 * @param {string|null} props.dueDate the deadline resolved from saved scopes.
 * @param {string|null} props.source which scope that deadline came from.
 * @param {string|null} props.pending an unsaved date already staged for it.
 * @param {number|null} props.ownId the id of the KPI's own saved date, if any.
 * @param {(kpiId: number, date: string) => void} props.onStage stages a date.
 * @param {(id: number) => void} props.onRemove removes the KPI's own saved date.
 * @param {() => void} props.onClose dismisses the editor.
 * @returns {JSX.Element}
 */
export default function DueDateEditorModal({
  kpi,
  dueDate,
  source,
  pending,
  ownId,
  onStage,
  onRemove,
  onClose,
}) {
  const [date, setDate] = useState(pending ?? dueDate ?? "");
  const isInherited = !pending && source && source !== "KPI";

  const save = () => {
    onStage(kpi.kpiId, date);
    onClose();
  };

  return (
    <div
      className="ddt-modal-backdrop"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="ddt-modal"
        role="dialog"
        aria-modal="true"
        aria-label={`Set due date for ${kpi.title}`}
      >
        <div className="ddt-modal-head">
          <h3 className="ddt-modal-title">Set due date</h3>
          <button
            type="button"
            className="ddt-modal-close"
            aria-label="Close"
            onClick={onClose}
          >
            ×
          </button>
        </div>

        <p className="ddt-modal-kpi">{kpi.title}</p>

        <p className="ddt-modal-current">
          Current: <strong>{formatDate(pending ?? dueDate)}</strong>
          {isInherited && (
            <span className="ddt-inherited">from {source.toLowerCase()}</span>
          )}
          {pending && <span className="ddt-badge">Unsaved</span>}
        </p>

        <label className="ddt-modal-label" htmlFor="ddt-editor-date">
          Due date
        </label>
        <input
          id="ddt-editor-date"
          type="date"
          className="ddt-date ddt-modal-date"
          value={date}
          min={todayISO()}
          onChange={(e) => setDate(e.target.value)}
        />

        {isInherited && date && (
          <p className="ddt-modal-hint">
            This replaces the {source.toLowerCase()} deadline for this KPI only.
          </p>
        )}

        <div className="ddt-modal-actions">
          {ownId && (
            <button
              type="button"
              className="ddt-btn ddt-btn-danger"
              onClick={() => onRemove(ownId)}
            >
              Remove saved date
            </button>
          )}
          {pending && (
            <button
              type="button"
              className="ddt-btn ddt-btn-ghost"
              onClick={() => {
                onStage(kpi.kpiId, "");
                onClose();
              }}
            >
              Discard staged date
            </button>
          )}
          <button
            type="button"
            className="ddt-btn ddt-btn-ghost"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="ddt-btn"
            disabled={!date}
            onClick={save}
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  );
}
